import React, { Component } from "react";
import { Link } from "react-router-dom";

// CSS
import "./About.scss";

// Components
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import Icon from "../../components/Icon";

// Helper
import { resolveTenant, cachedTenant } from "../../Helper/Api";
import { aboutOf, brandingOf } from "../../Helper/Branding";
import { t } from "../../Helper/i18n";

// Properties
const properties = require("../../properties.json");

// The page is driven by the tenant's own content (branding.aboutJson):
//   { title, role, photo, sections: [{ icon, title, sub, text, photo }], approach: { title, text, tag } }
// Every part is optional. A tenant with no aboutJson just gets their name + profile description.
class About extends Component {
  state = {
    tenant: cachedTenant(),
    notFound: false,
  };

  componentDidMount() {
    resolveTenant()
      .then((tenant) => {
        const site = brandingOf(tenant).siteTitle || properties.site_name;
        document.title = tenant.name ? t("aboutName", { name: tenant.name }) : site;
        this.setState({ tenant });
      })
      .catch(() => this.setState({ notFound: true }));
  }

  componentWillUnmount() {
    if (this.io) this.io.disconnect();
  }

  /* Reveal as each block is reached. Nothing hides until this has run, so a
     page whose script never arrives still reads in full. */
  watchReveal = (root) => {
    if (!root || this.io) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || !("IntersectionObserver" in window)) return;

    root.classList.add("js-reveal");
    this.io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-in");
          this.io.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.12 }
    );
    root.querySelectorAll("[data-reveal]").forEach((el) => this.io.observe(el));
  };

  render() {
    const { tenant, notFound } = this.state;

    if (notFound) {
      return (
        <div className="page">
          <div className="container text-center">
            <p>{t("notFound")}</p>
          </div>
        </div>
      );
    }

    if (!tenant) return null;

    const about = aboutOf(tenant) || {};
    const sections = Array.isArray(about.sections) ? about.sections : [];
    const title = about.title || tenant.name;
    const role = about.role || brandingOf(tenant).tagline;
    const intro = !about.sections && tenant.description ? tenant.description : null;
    const approach = about.approach;

    return (
      <>
        <div className="ab-stage mu mu-ground">
          <div className="ab-sky" aria-hidden="true">
            <span className="aur a1"></span>
            <span className="aur a2"></span>
            <span className="dots"></span>
          </div>

          <Header tenant={tenant} onStage />

          <div className="ab-hero">
            <div className="ab-copy">
              <span className="mu-chip"><Icon name="spark" className="chipi" /> {t("about")}</span>
              <h1>{title}</h1>
              {role && <p className="ab-role">{role}</p>}
              {intro && <p className="ab-intro">{intro}</p>}

              <div className="ab-acts">
                <Link to="/courses" className="mu-btn is-gold has-arw">
                  {t("seeCourses")} <Icon name="arrow" className="arw" />
                </Link>
                <Link to="/contact" className="mu-btn is-onteal">{t("getInTouch")}</Link>
              </div>
            </div>

          </div>
        </div>

        <div className="ab-body mu" ref={this.watchReveal}>
          {sections.length > 0 && (
            <div className="ab-grid">
              {sections.map((section, i) => (
                <article className="ab-card" key={section.title || i} data-reveal style={{ "--i": i % 3 }}>
                  {section.photo ? (
                    <span
                      className="ab-cardshot"
                      style={{ backgroundImage: `url(${section.photo})` }}
                      aria-hidden="true"
                    ></span>
                  ) : null}

                  <div className="ab-cardbody">
                    {section.icon && <span className="ab-ic" aria-hidden="true">{section.icon}</span>}
                    {section.title && <h3>{section.title}</h3>}
                    {section.sub && <span className="ab-sub">{section.sub}</span>}
                    {section.text && <p>{section.text}</p>}
                  </div>
                </article>
              ))}
            </div>
          )}

          {approach && (approach.text || approach.title) && (
            <blockquote className="ab-approach" data-reveal>
              <span className="ab-quote" aria-hidden="true">&ldquo;</span>
              {approach.title && <span className="ab-eyebrow">{approach.title}</span>}
              {approach.text && <p>{approach.text}</p>}
              {approach.tag && <cite>{approach.tag}</cite>}
            </blockquote>
          )}

          {/* nothing of their own written yet */}
          {sections.length === 0 && !intro && !approach && (
            <div className="ab-empty" data-reveal>
              <span className="ic" aria-hidden="true"><Icon name="spark" /></span>
              <b>{t("aboutSoon", { name: tenant.name })}</b>
              <p>{t("aboutEmpty")}</p>
              <Link to="/courses" className="mu-btn is-gold has-arw">
                {t("seeCourses")} <Icon name="arrow" className="arw" />
              </Link>
            </div>
          )}
        </div>

        <Footer tenant={tenant} />
      </>
    );
  }
}

export default About;
